import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GoogleCloudKeyFileJson,
  ExternalAccountCredentialJson,
  BigQueryAuthMethod,
  EnvScheduleForm,
  AssetSourceFormData,
  AssetSourcesUpdateApi,
  CreateAssetSourceApi,
  CreateEnvironmentApiRequest,
  EnvironmentForm,
  UpdateAssetSourceApi,
  UpdateEnvironmentApiRequest,
  DwhConnectionCredentials,
  FetchEnvironmentApi,
  WarehouseDbFilterFormData,
  WarehouseDbFiltersApi,
  SshTunnelConfig,
  DatabricksAuthMethod,
  DatabricksStorageAccess,
  DatabricksStorageAccessType,
} from 'shared/types';
import yaml from 'js-yaml';
import {
  CODE_PROJECTS,
  ENVIRONMENT,
  ENVIRONMENTS,
  ENVIRONMENTS_INTEGRATIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import {
  clearOptionalSecret,
  clearSecretIndication,
} from 'shared/utils/secretUtils';
import useEnvironments from 'shared/hooks/useEnvironments';
import * as api from '../../services/settingsApi';
import useSnackbar from '../useSnackbar';
import useSendTracking from './useSendTracking';

const buildSshConfig = (data: EnvironmentForm): SshTunnelConfig | null => {
  const supportsSsh =
    data.warehouseType === 'postgres' || data.warehouseType === 'redshift';
  if (!supportsSsh) {
    return null;
  }

  if (
    'sshEnabled' in data &&
    data.sshEnabled &&
    data.sshHost &&
    data.sshPort != null &&
    Number.isInteger(data.sshPort) &&
    data.sshPort >= 1 &&
    data.sshPort <= 65535 &&
    data.sshUsername
  ) {
    return {
      host: data.sshHost,
      port: data.sshPort,
      username: data.sshUsername,
    };
  }
  return null;
};

const buildCredentials = (
  data: EnvironmentForm,
): Partial<DwhConnectionCredentials> => {
  switch (data.warehouseType) {
    case 'redshift':
      return {
        type: 'redshift',
        host: data.redshiftHost!,
        user: data.redshiftUsername!,
        password: clearSecretIndication(data?.redshiftPassword),
        port: data.redshiftPort!,
      };
    case 'snowflake':
      return {
        type: 'snowflake',
        account: data.snowflakeAccount!,
        warehouse: data.snowflakeWarehouse!,
        role: data.snowflakeUserRole,
        user: data.snowflakeUser!,
        password: clearSecretIndication(data.snowflakePassword),
        private_key: clearSecretIndication(data.snowflakePrivateKey),
        private_key_passphrase: clearSecretIndication(
          data.snowflakePrivateKeyPassphrase,
        ),
      };
    case 'bigquery':
      if (data.bigqueryAuthMethod === BigQueryAuthMethod.WIF) {
        return {
          type: 'bigquery',
          external_account_json:
            data.bigqueryWifFile?.content != null
              ? (yaml.load(
                  data.bigqueryWifFile.content,
                ) as ExternalAccountCredentialJson)
              : undefined,
          external_account_file: data.bigqueryWifFile?.name,
          location: data.bigqueryLocation!,
        };
      }
      return {
        type: 'bigquery',
        keyfile_json:
          data.bigqueryAccountFile?.content != null
            ? (yaml.load(
                data.bigqueryAccountFile.content,
              ) as GoogleCloudKeyFileJson)
            : undefined,
        keyfile: data.bigqueryAccountFile?.name,
        location: data.bigqueryLocation!,
      };
    case 'postgres':
      return {
        type: 'postgres',
        host: data.postgresHost!,
        port: data.postgresPort!,
        user: data.postgresUser!,
        password: clearSecretIndication(data.postgresPassword),
      };
    case 'databricks': {
      const authMethod = data.databricksAuthMethod;
      const storageAccessType = data.databricksStorageAccessType;

      let storage_access: DatabricksStorageAccess | null | undefined =
        undefined;
      if (storageAccessType === DatabricksStorageAccessType.ACCESS_KEYS) {
        const secretAccessKey = clearOptionalSecret(
          data.databricksStorageSecretAccessKey,
        );
        const sessionToken = clearOptionalSecret(
          data.databricksStorageSessionToken,
        );
        storage_access = {
          storage_access_type: DatabricksStorageAccessType.ACCESS_KEYS,
          access_key_id: data.databricksStorageAccessKeyId!,
          ...(secretAccessKey != null && {
            secret_access_key: secretAccessKey,
          }),
          ...(sessionToken != null && {
            session_token: sessionToken,
          }),
        };
      } else if (storageAccessType === DatabricksStorageAccessType.ROLE_ARN) {
        const externalId = clearOptionalSecret(
          data.databricksStorageExternalId,
        );
        storage_access = {
          storage_access_type: DatabricksStorageAccessType.ROLE_ARN,
          role_arn: data.databricksStorageRoleArn!,
          ...(externalId != null && { external_id: externalId }),
        };
      } else if (
        storageAccessType === DatabricksStorageAccessType.CREDENTIALS_VENDING
      ) {
        storage_access = {
          storage_access_type: DatabricksStorageAccessType.CREDENTIALS_VENDING,
        };
      } else {
        storage_access = null;
      }

      if (authMethod === DatabricksAuthMethod.OAUTH) {
        return {
          type: 'databricks',
          host: data.databricksHost!,
          http_path: data.databricksHttpPath!,
          auth_type: DatabricksAuthMethod.OAUTH,
          client_id: data.databricksClientId!,
          client_secret: clearSecretIndication(data.databricksClientSecret),
          storage_access,
        };
      }
      return {
        type: 'databricks',
        host: data.databricksHost!,
        http_path: data.databricksHttpPath!,
        auth_type: DatabricksAuthMethod.TOKEN,
        token: clearSecretIndication(data.databricksAccessToken),
        storage_access,
      };
    }
    case 'clickhouse':
      return {
        type: 'clickhouse',
        host: data.clickhouseHost!,
        port: data.clickhousePort!,
        user: data.clickhouseUser!,
        password: clearSecretIndication(data.clickhousePassword),
      };
    case 'athena':
      return {
        type: 'athena',
        region: data.athenaRegion!,
        workgroup: data.athenaWorkgroup!,
        s3_staging_dir: data.athenaS3StagingDir!,
        aws_access_key_id: data.athenaAccessKeyId,
        aws_secret_access_key: clearSecretIndication(
          data.athenaSecretAccessKey,
        ),
        aws_role_arn: data.athenaRoleArn,
        aws_external_id: clearOptionalSecret(data.athenaExternalId),
      };
    case 'dremio':
      return {
        type: 'dremio',
        host: data.dremioHost as 'api.dremio.cloud' | 'api.eu.dremio.cloud',
        project_id: data.dremioProjectId!,
        user: data.dremioUser!,
        token: clearSecretIndication(data.dremioToken),
      };
    case 'fabric':
      return {
        type: 'fabric',
        server: data.fabricServer!,
        port: data.fabricPort!,
        tenant_id: data.fabricTenantId!,
        client_id: data.fabricClientId!,
        client_secret: clearSecretIndication(data.fabricClientSecret),
      };
    default:
      return {};
  }
};

const serializeDbFilters = (
  dbFilters: WarehouseDbFilterFormData[],
): WarehouseDbFiltersApi[] =>
  dbFilters.map((filter) => ({
    database: filter.database,
    schemas: filter.schemas.length > 0 ? filter.schemas : null,
  }));

const serializeAssetSourceForCreate = (
  source: AssetSourceFormData,
): CreateAssetSourceApi => {
  if (source.type === 'dbt') {
    return {
      type: 'dbt',
      name: source.name,
      schema: source.schema,
      database: source.database,
    };
  }
  return {
    type: 'warehouse',
    name: source.name,
    filters: { dbFilters: serializeDbFilters(source.dbFilters) },
  };
};

const serializeAssetSourceForUpdate = (
  source: AssetSourceFormData,
): UpdateAssetSourceApi | undefined => {
  if (!source.id) {
    return undefined;
  }
  if (source.type === 'dbt') {
    return {
      type: 'dbt',
      id: source.id,
      name: source.name,
    };
  }
  const name = source.name.trim();
  return {
    type: 'warehouse',
    id: source.id,
    ...(name ? { name } : {}),
    filters: { dbFilters: serializeDbFilters(source.dbFilters) },
  };
};

const calculateAssetSourcesChangesForUpdate = (
  formSources: AssetSourceFormData[],
  existingSources: AssetSourceFormData[],
): AssetSourcesUpdateApi => {
  const existingIds = new Set(
    existingSources.map((source) => source.id).filter(Boolean),
  );

  const add = formSources
    .filter((source) => !source.id || !existingIds.has(source.id))
    .map(serializeAssetSourceForCreate);

  const update = formSources
    .filter((source) => source.id && existingIds.has(source.id))
    .map(serializeAssetSourceForUpdate)
    .filter((source): source is UpdateAssetSourceApi => source != null);

  const formIds = new Set(
    formSources.map((source) => source.id).filter(Boolean),
  );
  const deleteSources = existingSources
    .filter((source) => source.id && !formIds.has(source.id))
    .map((source) => ({ id: source.id! }));

  return {
    add: add.length > 0 ? add : undefined,
    update: update.length > 0 ? update : undefined,
    delete: deleteSources.length > 0 ? deleteSources : undefined,
  };
};

export const toFormAssetSources = (
  sources: FetchEnvironmentApi['assetSources'],
): AssetSourceFormData[] =>
  sources.map((source) => {
    if (source.type === 'dbt') {
      return {
        id: source.id,
        type: 'dbt',
        name: source.name,
        schema: source.schema,
        database: source.database ?? undefined,
      };
    }
    return {
      id: source.id,
      type: 'warehouse',
      name: source.name,
      dbFilters: source.filters.dbFilters.map((filter) => ({
        database: filter.database,
        schemas: filter.schemas ?? [],
      })),
    };
  });

export type EnvironmentMutationInput = EnvironmentForm & {
  existingEnvironmentData?: FetchEnvironmentApi;
};

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useEnvironmentsSetup = ({ envId, onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const {
    data: environmentsData,
    selectedEnvId,
    setSelectedEnv,
  } = useEnvironments();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const environmentMutation = useMutation(
    (input: EnvironmentMutationInput): Promise<void> => {
      const { existingEnvironmentData, ...data } = input;
      const credentials = buildCredentials(data);
      const sshConfig = buildSshConfig(data);

      if (envId) {
        const assetSourcesChanges = existingEnvironmentData
          ? calculateAssetSourcesChangesForUpdate(
              data.assetSources,
              toFormAssetSources(existingEnvironmentData.assetSources),
            )
          : undefined;

        const updateRequest: UpdateEnvironmentApiRequest = {
          name: data.environmentName,
          environmentType: data.environmentType,
          dwhConnection: {
            warehouseType: data.warehouseType,
            credentials: credentials as DwhConnectionCredentials,
            ssh: sshConfig,
          },
          assetSources: assetSourcesChanges,
        };

        return api.updateEnvironment(envId, updateRequest);
      } else {
        // Create new environment
        const createRequest: CreateEnvironmentApiRequest = {
          name: data.environmentName,
          environmentType: data.environmentType,
          dwhConnection: {
            warehouseType: data.warehouseType,
            credentials: credentials as DwhConnectionCredentials,
            ssh: sshConfig,
          },
          assetSources: data.assetSources.map(serializeAssetSourceForCreate),
        };

        return api.createEnvironment(createRequest);
      }
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([ENVIRONMENTS]);
        queryClient.invalidateQueries([ENVIRONMENT, envId]);
        queryClient.invalidateQueries([CODE_PROJECTS, envId]);
        if (!envId) {
          queryClient.invalidateQueries([ENVIRONMENTS_INTEGRATIONS]);
        }
        openSnackbar({
          message: envId
            ? 'Environment updated successfully'
            : 'Environment created successfully',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.CONNECT_DWH_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_DWH_FAILURE,
        }),
    },
  );

  const envScheduleMutation = useMutation(
    (data: EnvScheduleForm) => {
      const reqData = {
        enabled: data.updateMethod === 'webhook',
        token: data.updateMethod === 'webhook' ? data.token : null,
      };

      return api.setWebhook(reqData, envId!);
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([ENVIRONMENTS]);
        openSnackbar({
          message: 'Successfully updated environment schedule settings',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName:
            trackingEvents?.CREATE_ENVIRONMENT_SYNC_WEBHOOK_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CREATE_ENVIRONMENT_SYNC_WEBHOOK_FAILURE,
        }),
    },
  );

  const deleteEnvironmentMutation = useMutation(
    () => {
      if (!envId) {
        throw new Error('Environment ID is required for deletion');
      }
      return api.deleteEnvironment(envId);
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();

        if (envId && selectedEnvId === envId) {
          const remainingEnvironments =
            environmentsData?.filter(
              (environment) => environment.id !== envId,
            ) ?? [];
          const nextEnvironment = remainingEnvironments[0];

          if (nextEnvironment) {
            setSelectedEnv(nextEnvironment.id);
          } else {
            setSelectedEnv();
          }
        }

        queryClient.invalidateQueries([ENVIRONMENTS]);
        queryClient.invalidateQueries([ENVIRONMENTS_INTEGRATIONS]);
        openSnackbar({
          message: 'Environment deleted successfully',
          variant: 'success',
        });
      },
      onError: () => {
        openSnackbar({
          message: 'Failed to delete environment',
          variant: 'error',
        });
      },
    },
  );

  return {
    environmentMutation,
    envScheduleMutation,
    deleteEnvironmentMutation,
  };
};

export default useEnvironmentsSetup;
